import { Link } from 'react-router';
import { ChevronDown, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import internalLogo from '@/assets/header-logo-internal.svg';
import externalLogo from '@/assets/header-logo-external.svg';

type PageHeaderProps = {
  variant?: 'external' | 'internal';
  /** Signed-in user's display name; renders the account dropdown when present. */
  userName?: string;
  /** Route the logout menu item navigates to. */
  logoutHref?: string;
  className?: string;
};

/**
 * App-level top bar. The logo block (left) links home and shows the product
 * brand line plus the management-screen title; the account dropdown (right)
 * carries the signed-in user's name and the logout action. `internal` paints
 * the brand-red shell for signed-in chrome; `external` uses the sidebar surface.
 */
export const PageHeader = ({
  variant = 'internal',
  userName,
  logoutHref,
  className,
}: PageHeaderProps) => {
  const { t } = useTranslation();
  const isInternal = variant === 'internal';

  return (
    <header
      data-variant={variant}
      className={cn(
        'flex h-16 w-full flex-row items-center justify-between gap-4 px-6',
        isInternal ? 'bg-branding-red text-white' : 'bg-sidebar text-sidebar-foreground',
        className
      )}
    >
      <Link
        to='/'
        aria-label={t('header.title')}
        className={cn(
          'flex justify-center rounded-md transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          isInternal && 'focus-visible:ring-offset-branding-red'
        )}
      >
        <img
          src={isInternal ? internalLogo : externalLogo}
          alt={t('header.title')}
          className='h-10 w-auto'
        />
      </Link>

      {userName && logoutHref && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='outline'
              className={cn(
                'rounded-full bg-transparent',
                isInternal &&
                  'border-white text-white hover:bg-white/10 hover:text-white focus-visible:ring-offset-branding-red'
              )}
            >
              {userName}
              <ChevronDown className='size-4' aria-hidden='true' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem asChild>
              <Link to={logoutHref}>
                <LogOut className='size-4' aria-hidden='true' />
                {t('navigation.menu.logout')}
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </header>
  );
};
